import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useEditorStore } from "@/stores/editor/editor-store";
import { Component } from "@/types";

interface UndoRedoOperation {
  type:
    | "drag_move"
    | "component_add"
    | "component_delete"
    | "component_update"
    | "multi_component";
  timestamp: Date;
  data: any;
  description: string;
}

interface UndoRedoContextValue {
  recordDragOperation: (
    componentId: string,
    from: { x: number; y: number },
    to: { x: number; y: number },
  ) => void;
  recordComponentAdd: (component: Component) => void;
  recordComponentDelete: (component: Component) => void;
  recordComponentUpdate: (
    componentId: string,
    oldProps: Partial<Component>,
    newProps: Partial<Component>,
  ) => void;
  startBatch: () => void;
  endBatch: (description: string) => void;
  getOperationDescription: (operation: UndoRedoOperation) => string;
}

const UndoRedoContext = createContext<UndoRedoContextValue | null>(null);

export const useUndoRedo = () => {
  const context = useContext(UndoRedoContext);
  if (!context) {
    throw new Error("useUndoRedo must be used within UndoRedoProvider");
  }
  return context;
};

interface UndoRedoProviderProps {
  children: React.ReactNode;
}

export const UndoRedoProvider: React.FC<UndoRedoProviderProps> = ({
  children,
}) => {
  const { pushToHistory, components } = useEditorStore();

  const batchOperations = useRef<UndoRedoOperation[]>([]);
  const isBatching = useRef(false);
  const pendingOperations = useRef<NodeJS.Timeout | null>(null);

  // Debounced history push to avoid too many undo states during drag
  const debouncedPushToHistory = useCallback(() => {
    if (pendingOperations.current) {
      clearTimeout(pendingOperations.current);
    }

    pendingOperations.current = setTimeout(() => {
      pushToHistory();
      pendingOperations.current = null;
    }, 300); // 300ms debounce
  }, [pushToHistory]);

  // Record drag move operations
  const recordDragOperation = useCallback(
    (
      componentId: string,
      from: { x: number; y: number },
      to: { x: number; y: number },
    ) => {
      const component = components.find((c) => c.id === componentId);
      if (!component) return;

      const operation: UndoRedoOperation = {
        type: "drag_move",
        timestamp: new Date(),
        data: {
          componentId,
          componentName: component.name,
          from,
          to,
          distance: Math.sqrt(
            Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2),
          ),
        },
        description: `Moved ${component.name} from (${from.x}, ${from.y}) to (${to.x}, ${to.y})`,
      };

      if (isBatching.current) {
        batchOperations.current.push(operation);
      } else {
        // For drag operations, use debounced history push
        debouncedPushToHistory();
      }
    },
    [components, debouncedPushToHistory],
  );

  // Record component addition
  const recordComponentAdd = useCallback(
    (component: Component) => {
      const operation: UndoRedoOperation = {
        type: "component_add",
        timestamp: new Date(),
        data: {
          componentId: component.id,
          componentName: component.name,
          componentType: component.type,
          position: component.position,
        },
        description: `Added ${component.type} component "${component.name}"`,
      };

      if (isBatching.current) {
        batchOperations.current.push(operation);
      } else {
        // Immediate history push for component operations
        pushToHistory();
      }
    },
    [pushToHistory],
  );

  // Record component deletion
  const recordComponentDelete = useCallback(
    (component: Component) => {
      const operation: UndoRedoOperation = {
        type: "component_delete",
        timestamp: new Date(),
        data: {
          componentId: component.id,
          componentName: component.name,
          componentType: component.type,
          position: component.position,
          fullComponent: { ...component }, // Store full component for potential restore
        },
        description: `Deleted ${component.type} component "${component.name}"`,
      };

      if (isBatching.current) {
        batchOperations.current.push(operation);
      } else {
        pushToHistory();
      }
    },
    [pushToHistory],
  );

  // Record component property updates
  const recordComponentUpdate = useCallback(
    (
      componentId: string,
      oldProps: Partial<Component>,
      newProps: Partial<Component>,
    ) => {
      const component = components.find((c) => c.id === componentId);
      if (!component) return;

      // Create a description of what changed
      const changedFields = Object.keys(newProps).filter(
        (key) =>
          JSON.stringify(oldProps[key]) !== JSON.stringify(newProps[key]),
      );

      const operation: UndoRedoOperation = {
        type: "component_update",
        timestamp: new Date(),
        data: {
          componentId,
          componentName: component.name,
          oldProps,
          newProps,
          changedFields,
        },
        description: `Updated ${component.name}: ${changedFields.join(", ")}`,
      };

      if (isBatching.current) {
        batchOperations.current.push(operation);
      } else {
        debouncedPushToHistory();
      }
    },
    [components, debouncedPushToHistory],
  );

  // Start batching operations (useful for multi-step operations)
  const startBatch = useCallback(() => {
    isBatching.current = true;
    batchOperations.current = [];
  }, []);

  // End batching and create a single undo operation
  const endBatch = useCallback(
    (description: string) => {
      if (!isBatching.current) return;

      isBatching.current = false;

      if (batchOperations.current.length > 0) {
        const batchOperation: UndoRedoOperation = {
          type: "multi_component",
          timestamp: new Date(),
          data: {
            operations: [...batchOperations.current],
            operationCount: batchOperations.current.length,
          },
          description:
            description ||
            `Batch operation (${batchOperations.current.length} actions)`,
        };

        // Clear any pending debounced operations
        if (pendingOperations.current) {
          clearTimeout(pendingOperations.current);
          pendingOperations.current = null;
        }

        pushToHistory();
      }

      batchOperations.current = [];
    },
    [pushToHistory],
  );

  // Generate human-readable description for operations
  const getOperationDescription = useCallback(
    (operation: UndoRedoOperation) => {
      switch (operation.type) {
        case "drag_move": {
          const distance = Math.round(operation.data.distance);
          return `Moved ${operation.data.componentName} ${distance}px`;
        }

        case "component_add": {
          return `Added ${operation.data.componentType}`;
        }

        case "component_delete": {
          return `Deleted ${operation.data.componentName}`;
        }

        case "component_update": {
          const fields = operation.data.changedFields.slice(0, 2).join(", ");
          const hasMore = operation.data.changedFields.length > 2;
          return `Updated ${operation.data.componentName} (${fields}${hasMore ? "..." : ""})`;
        }

        case "multi_component": {
          return operation.description;
        }

        default: {
          return operation.description || "Unknown operation";
        }
      }
    },
    [],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pendingOperations.current) {
        clearTimeout(pendingOperations.current);
      }
    };
  }, []);

  const contextValue: UndoRedoContextValue = {
    recordDragOperation,
    recordComponentAdd,
    recordComponentDelete,
    recordComponentUpdate,
    startBatch,
    endBatch,
    getOperationDescription,
  };

  return (
    <UndoRedoContext.Provider value={contextValue}>
      {children}
    </UndoRedoContext.Provider>
  );
};
